import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../../../shared/components/Button';
import { describeError, isConflict, isTerminalStatus } from '../describeError';
import { useIsMounted } from '../hooks/useIsMounted';
import { formatDateRange, sourceSystemLabel } from '../format';
import { payrollHistoryImportGateway as gateway } from '../index';
import { batchStepPath, stepForBatch } from '../routes';
import type { BatchDetail, Capabilities } from '../types';
import { ErrorSummary } from './basics';

export const CONFIRMATION_TEXT =
  'I understand that this import adds historical payroll records only. It will not initiate payments, tax deposits, tax filings, or accounting entries.';

interface Props {
  batch: BatchDetail;
  capabilities: Capabilities;
  announce: (message: string) => void;
  onBatchChange: (batch: BatchDetail) => void;
}

export function ConfirmStep({ batch, capabilities, announce, onBatchChange }: Props) {
  const navigate = useNavigate();
  const mounted = useIsMounted();
  const [accepted, setAccepted] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const commit = async () => {
    setCommitting(true);
    setError(null);
    announce('Importing historical payroll records…');
    try {
      const result = await gateway.commit(batch.id, {
        confirmationAccepted: accepted,
        expectedConcurrencyVersion: batch.concurrencyVersion,
      });
      if (!mounted.current) return;
      navigate(batchStepPath(batch.id, 'results'));
      onBatchChange(result);
      announce('Import finished.');
    } catch (err) {
      // ANY commit error can leave the batch in a new state (rolled back -> Failed, revalidation failed ->
      // ValidationFailed, already committed elsewhere -> Completed). Refetch it, then decide where the user belongs.
      const message = describeError(err, 'Could not commit this import.');
      let latest: BatchDetail | null = null;
      try {
        latest = await gateway.getBatch(batch.id);
      } catch {
        /* keep the explanation below; the next action will surface a persistent load problem */
      }
      if (!mounted.current) return;

      if (latest && (isTerminalStatus(latest.status) || latest.status !== 'ReadyForReview')) {
        // The step the batch is now on is where its reason is shown (Results shows failureReason, Validate shows
        // the blocking errors and failureReason) - stay here and the explanation would simply vanish.
        navigate(batchStepPath(batch.id, stepForBatch(latest)));
        onBatchChange(latest);
        announce(isTerminalStatus(latest.status) ? `The import ended: ${latest.status}.` : 'The import needs attention.');
        return;
      }

      // Still ReadyForReview (e.g. a stale version or a transient fault): keep the error here with fresh data.
      setError(
        isConflict(err) && latest ? `${message} The latest details were reloaded.` : message,
      );
      setAccepted(false);
      if (latest) onBatchChange(latest);
      setCommitting(false);
    }
  };

  return (
    <div>
      {error && <ErrorSummary message={error} />}
      <dl className="phi-facts" aria-label="Import to be added">
        <dt>File</dt><dd>{batch.fileName ?? '—'}</dd>
        <dt>Source platform</dt><dd>{sourceSystemLabel(batch.sourceSystem)}</dd>
        <dt>Payroll runs</dt><dd>{batch.payrollRunCount}</dd>
        <dt>Employees</dt><dd>{batch.employeeCount}</dd>
        <dt>Pay date range</dt><dd>{formatDateRange(batch.payDateStart, batch.payDateEnd)}</dd>
        <dt>Warnings</dt><dd>{batch.warningCount}</dd>
      </dl>

      <div className="phi-checkbox">
        <input
          id="phi-confirm"
          type="checkbox"
          checked={accepted}
          disabled={!capabilities.canCommit || committing}
          onChange={(e) => setAccepted(e.target.checked)}
        />
        <label htmlFor="phi-confirm">{CONFIRMATION_TEXT}</label>
      </div>

      {!capabilities.canCommit && (
        <div className="phi-notice">
          <p>You can prepare this import, but you don&apos;t have permission to commit it.</p>
          <p className="muted">A user with commit permission must open this import and finish it.</p>
        </div>
      )}

      <div className="phi-footer">
        <Link to={batchStepPath(batch.id, 'problems')} className="phi-btn-secondary">Back</Link>
        <Button
          type="button"
          disabled={!accepted || !capabilities.canCommit}
          loading={committing}
          loadingLabel="Importing…"
          onClick={() => void commit()}
        >
          Import
        </Button>
      </div>
    </div>
  );
}
